/* eslint-disable */
import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { X, Send, Copy, Check, CheckCircle } from 'lucide-react';
import { C, inp, lbl, genToken, fmt } from './teamUtils';

const ONBOARDING_ROLES = ['Server', 'Barista', 'Kitchen', 'FOH Manager'];

export default function SendOnboardingModal({ onClose, currentUser, member }) {
  const [name,    setName]    = useState(member?.name  || '');
  const [email,   setEmail]   = useState(member?.email || '');
  const [role,    setRole]    = useState(() => {
    if (!member) return 'Server';
    const r = (member.role || '').toLowerCase();
    if (r.includes('barista') || r.includes('bar')) return 'Barista';
    if (r.includes('chef') || r.includes('kitchen') || member.dept === 'KIT') return 'Kitchen';
    return 'Server';
  });
  const [venue,    setVenue]   = useState(member?.venue || 'Box Jordaan');
  const [sending,  setSending] = useState(false);
  const [sent,     setSent]    = useState(false);
  const [link,     setLink]    = useState('');
  const [copied,   setCopied]  = useState(false);
  const [error,    setError]   = useState('');

  // Existing invite status
  const [existingInvite, setExistingInvite] = useState(null);
  const [loadingInvite,  setLoadingInvite]  = useState(!!member?.id);

  // Load existing invite for this member on open
  useEffect(() => {
    if (!member?.id) { setLoadingInvite(false); return; }
    async function loadInvite() {
      const { data } = await supabase
        .from('onboarding_invites')
        .select('*')
        .eq('member_id', member.id)
        .eq('type', 'pack')
        .order('sent_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      setExistingInvite(data || null);
      setLoadingInvite(false);
    }
    loadInvite();
  }, [member?.id]);

  async function handleSend() {
    if (!name.trim())  { setError("Please enter the team member's name."); return; }
    if (!email.trim()) { setError('Please enter an email address.'); return; }
    setSending(true); setError('');
    const token   = genToken();
    const packUrl = `${window.location.origin}/welcome/${token}`;
    const { error: dbErr } = await supabase.from('onboarding_invites').insert({
      token,
      type:     'pack',
      role,
      name:     name.trim(),
      email:    email.trim(),
      venue,
      sent_by:  currentUser?.name || 'Manager',
      sent_at:  new Date().toISOString(),
      member_id: member?.id || null,
    });
    if (dbErr) { setError('Failed to create invite: ' + dbErr.message); setSending(false); return; }
    const { error: fnErr } = await supabase.functions.invoke('send-onboarding-email', {
      body: { to: email.trim(), name: name.trim(), packUrl, role, sentBy: currentUser?.name || 'Manager' },
    }).catch(() => ({ error: true }));
    if (fnErr) setError('Email could not be sent — copy the link below and share manually:');
    setLink(packUrl); setSent(true); setSending(false);
    // Refresh existing invite display
    setExistingInvite({ sent_at: new Date().toISOString(), email: email.trim(), completed_at: null, opened_at: null });
  }

  async function copyLink() {
    await navigator.clipboard.writeText(link);
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  }

  // Status badge for existing invite
  function InviteStatusBadge({ invite }) {
    if (invite.completed_at) {
      return (
        <div style={{display:'flex',alignItems:'center',gap:'8px',padding:'10px 14px',background:C.greenLight,border:`1px solid ${C.green}44`,borderRadius:'8px'}}>
          <CheckCircle size={16} color={C.green}/>
          <div style={{flex:1}}>
            <div style={{fontSize:'13px',fontWeight:600,color:C.green}}>Pack confirmed by {invite.name || member?.name}</div>
            <div style={{fontSize:'11px',color:C.mid,marginTop:'1px'}}>
              Sent {fmt(invite.sent_at)} · Confirmed {fmt(invite.completed_at)}
            </div>
          </div>
        </div>
      );
    }
    if (invite.opened_at) {
      return (
        <div style={{display:'flex',alignItems:'center',gap:'8px',padding:'10px 14px',background:C.amberLight,border:`1px solid ${C.amber}44`,borderRadius:'8px'}}>
          <div style={{width:16,height:16,borderRadius:'50%',background:C.amber,flexShrink:0}}/>
          <div style={{flex:1}}>
            <div style={{fontSize:'13px',fontWeight:600,color:C.amber}}>Pack opened but not confirmed</div>
            <div style={{fontSize:'11px',color:C.mid,marginTop:'1px'}}>
              Sent {fmt(invite.sent_at)} · Opened {fmt(invite.opened_at)}
            </div>
          </div>
        </div>
      );
    }
    return (
      <div style={{display:'flex',alignItems:'center',gap:'8px',padding:'10px 14px',background:C.cream,border:`1px solid ${C.border}`,borderRadius:'8px'}}>
        <div style={{width:16,height:16,borderRadius:'50%',background:C.border,flexShrink:0}}/>
        <div style={{flex:1}}>
          <div style={{fontSize:'13px',fontWeight:600,color:C.charcoal}}>Pack sent — awaiting confirmation</div>
          <div style={{fontSize:'11px',color:C.mid,marginTop:'1px'}}>
            Sent {fmt(invite.sent_at)} to {invite.email}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:700,display:'flex',alignItems:'center',justifyContent:'center',padding:'16px'}} onClick={onClose}>
      <div style={{background:C.white,borderRadius:'16px',width:'100%',maxWidth:'460px',boxShadow:'0 24px 64px rgba(0,0,0,0.2)',overflow:'hidden'}} onClick={e=>e.stopPropagation()}>

        <div style={{padding:'20px 24px',background:C.navy,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
          <div>
            <div style={{fontWeight:700,fontSize:'15px',color:C.white}}>Send Onboarding Pack</div>
            <div style={{fontSize:'12px',color:C.skyBlue,marginTop:'2px'}}>Team member reads & confirms online</div>
          </div>
          <button onClick={onClose} style={{background:'rgba(255,255,255,0.12)',border:'none',borderRadius:'8px',cursor:'pointer',color:C.white,padding:'6px',display:'flex'}}><X size={18}/></button>
        </div>

        <div style={{padding:'24px',display:'flex',flexDirection:'column',gap:'14px'}}>

          {/* Existing invite status */}
          {loadingInvite && (
            <div style={{fontSize:'12px',color:C.mid}}>Checking existing pack status…</div>
          )}
          {!loadingInvite && existingInvite && !sent && (
            <div>
              <div style={{fontSize:'11px',fontWeight:700,color:C.mid,textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:'6px'}}>
                Previous pack
              </div>
              <InviteStatusBadge invite={existingInvite}/>
              {!existingInvite.completed_at && (
                <div style={{fontSize:'11px',color:C.mid,marginTop:'6px',fontStyle:'italic'}}>
                  You can resend below — this will create a new link.
                </div>
              )}
            </div>
          )}

          {/* Send form or confirmation */}
          {!sent ? (
            <>
              {error && (
                <div style={{background:C.amberLight,border:`1px solid ${C.amber}44`,borderRadius:'8px',padding:'10px 14px',fontSize:'13px',color:C.amber}}>
                  {error}
                </div>
              )}
              <div><label style={lbl}>Full Name</label><input value={name} onChange={e=>setName(e.target.value)} placeholder="Jan de Vries" style={inp}/></div>
              <div><label style={lbl}>Email Address</label><input type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="jan@email.com" style={inp}/></div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
                <div>
                  <label style={lbl}>Role / Pack</label>
                  <select value={role} onChange={e=>setRole(e.target.value)} style={inp}>
                    {ONBOARDING_ROLES.map(r=><option key={r}>{r}</option>)}
                  </select>
                </div>
                <div>
                  <label style={lbl}>Venue</label>
                  <select value={venue} onChange={e=>setVenue(e.target.value)} style={inp}>
                    {['Box Jordaan','Box Plantage','Sassys','Prep Kitchen'].map(v=><option key={v}>{v}</option>)}
                  </select>
                </div>
              </div>
              <button onClick={handleSend} disabled={sending}
                style={{padding:'12px',background:C.navy,color:C.white,border:'none',borderRadius:'10px',fontSize:'14px',fontWeight:600,cursor:sending?'not-allowed':'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:'8px',marginTop:'4px'}}>
                <Send size={15}/>{sending ? 'Sending…' : existingInvite && !existingInvite.completed_at ? 'Resend Onboarding Pack' : 'Send Onboarding Pack'}
              </button>
            </>
          ) : (
            <>
              <div style={{textAlign:'center',padding:'12px 0'}}>
                <CheckCircle size={40} color={C.green} style={{marginBottom:'12px'}}/>
                <div style={{fontWeight:700,fontSize:'15px',color:C.charcoal,marginBottom:'6px'}}>Pack sent to {name}!</div>
                <div style={{fontSize:'13px',color:C.mid}}>They'll receive a link to read and confirm their onboarding pack.</div>
              </div>
              {link && (
                <div style={{background:C.cream,borderRadius:'10px',padding:'14px',border:`1px solid ${C.border}`}}>
                  <div style={{fontSize:'11px',fontWeight:700,color:C.mid,marginBottom:'8px',textTransform:'uppercase',letterSpacing:'0.05em'}}>
                    {error ? 'Share this link manually' : 'Or copy link to share directly'}
                  </div>
                  <div style={{display:'flex',gap:'8px',alignItems:'center'}}>
                    <div style={{flex:1,fontSize:'12px',color:C.charcoal,background:C.white,border:`1px solid ${C.border}`,borderRadius:'6px',padding:'8px 10px',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{link}</div>
                    <button onClick={copyLink}
                      style={{padding:'8px 12px',background:copied?C.green:C.navy,color:C.white,border:'none',borderRadius:'8px',cursor:'pointer',fontSize:'12px',fontWeight:600,whiteSpace:'nowrap',display:'flex',alignItems:'center',gap:'6px',flexShrink:0}}>
                      {copied ? <><Check size={13}/>Copied!</> : <><Copy size={13}/>Copy</>}
                    </button>
                  </div>
                </div>
              )}
              <button onClick={onClose}
                style={{padding:'10px',background:C.cream,color:C.navy,border:`1px solid ${C.border}`,borderRadius:'10px',fontSize:'13px',fontWeight:600,cursor:'pointer'}}>
                Close
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}